// The map: a PixiJS scene of what the player can see, with touch-first camera controls.
// Everything is rebuilt from the PlayerView (no client-side game state); the ticker only
// animates: the Signal pulsing along relays, ships in flight, the selection reticle.
import { Application, Container, Graphics, Sprite, Text, TextStyle, type Texture } from 'pixi.js';
import { hexDisk, hexToPixel, SECTOR_SIZE, type PlayerView, type RelayView, type SystemView } from '@aurane/sim';
import { expeditionsOn } from '../ui/expeditionStore.js';
import { frameOn, networkFocus, sectorTier, type Insets, type NetworkFocus, type SectorTier } from './focus.js';
import { fxLevel, networkDepth, relayChanges, seasonProgress, silenceFront, sourceEnd } from './signal.js';
import { SignalFx } from './signalFx.js';
import { cargoTexture, coreTexture, dotTexture, glowTexture, glyphTexture, nebulaTexture, pulseTexture, ringTexture, scoutTexture, shipTexture } from './textures.js';

export const FACTION_COLOR: Record<string, number> = { concordat: 0xe8c872, guild: 0xe07a3f, oracles: 0x9b7bff, corsairs: 0xd9534f };
export const RESOURCE_COLOR: Record<string, number> = { metal: 0xb8c4d0, energy: 0xffd166, food: 0x7ee2a8, crystal: 0x8be9ff, rium: 0xc58cff };
const STAR_TINT: Record<string, number> = { metal: 0xdfe7f0, energy: 0xffe9a8, food: 0xd8ffe8, crystal: 0xd6f7ff };
const HEX_SIZE = SECTOR_SIZE * 0.62;
const SIGNAL = 0x7dd3fc;
const BRIDGE = 0xffd166;
const DANGER = 0xff5252;
const WORMHOLE = 0xc084fc;
const TIMER_STYLE = new TextStyle({ fill: 0x7dd3fc, fontSize: 26, fontFamily: 'Rajdhani, system-ui, sans-serif', fontWeight: '700', stroke: { color: 0x05070f, width: 6 } });
/** « 7:05 » on a relay under construction (real seconds), « … » once the server has not yet said it is live. */
export const countdown = (seconds: number): string => {
  if (seconds <= 0) return '…';
  const s = Math.ceil(seconds), m = Math.floor(s / 60);
  return m >= 60 ? `${Math.floor(m / 60)}h${String(m % 60).padStart(2, '0')}` : `${m}:${String(s % 60).padStart(2, '0')}`;
};
/** The capital's colour: the amber of the art direction, whatever the faction. */
const CAPITAL = 0xffc857;

export interface MapCallbacks {
  onSelect(systemId: string | null): void;
  /** Screen pixels hidden by the HUD and the panel, so the first frame lands in the part of the map one can see. */
  insets?(): Insets;
}

interface Pulse { sprite: Sprite; ax: number; ay: number; bx: number; by: number; t: number; speed: number }
interface Ship { sprite: Sprite; fromX: number; fromY: number; toX: number; toY: number; departAt: number; arriveAt: number }

export class GalaxyMap {
  readonly app = new Application();
  private world = new Container();
  private starfield = new Container();
  private fogLayer = new Graphics();
  private sectorLayer = new Graphics();
  /** The lit neighbourhood (issue #39): wide static nebula washes over the home sectors and the ring around them. */
  private lightLayer = new Container();
  private nebulaLayer = new Container();
  private holeLayer = new Container();
  private relayGlow = new Graphics();
  private relayLayer = new Graphics();
  private pulseLayer = new Container();
  private systemLayer = new Container();
  private fleetLayer = new Container();
  private reticle = new Graphics();
  /** Flashes live in their own layer: the render pass rebuilds the others on every view. */
  private flashLayer = new Container();
  private labelLayer = new Container();
  private view: PlayerView | null = null;
  private selected: string | null = null;
  private linkFrom: string | null = null;
  private factionOf = new Map<string, string>();
  private systemById = new Map<string, SystemView>();
  private ready = false;
  private pointers = new Map<number, { x: number; y: number }>();
  private pinchDist = 0;
  private dragMoved = false;
  private pulses: Pulse[] = [];
  private ships: Ship[] = [];
  private rings: Sprite[] = [];
  private simTimeAtReceive = 0;
  private receivedAt = 0;
  private lastLabelScale = -1;
  private tex!: { glow: Texture; core: Texture; dot: Texture; ring: Texture; ship: Texture; cargo: Texture; pulse: Texture; scout: Texture };
  /** Wormhole mouths I know (issue #48): a slow violet swirl at both ends. */
  private swirls: Graphics[] = [];
  private engagedRings: Graphics[] = [];
  /** Expanding rings on a star where the General just acted (Counsel "Do it"): the player sees where it happened. */
  private flashes: { g: Graphics; x: number; y: number; t: number }[] = [];
  /** The capital's slow beacon ring (alpha only, animated; no filter). */
  private capitalPulse: Graphics | null = null;
  private focus: NetworkFocus | null = null;
  /** Countdowns on my relays under construction (issue #48): text refreshed from the sim clock, not rebuilt. */
  private relayTimers: { text: Text; readyAt: number }[] = [];
  private lastTimerRefresh = 0;
  /** A moment's camera flight and its Signal run to the capital (issue #48); both can be cut short by a tap. */
  private tween: { fx: number; fy: number; tx: number; ty: number; t: number; ms: number } | null = null;
  private run: { sprite: Sprite; pts: { x: number; y: number }[]; t: number; ms: number; done: () => void } | null = null;
  /** The camera frames the network once, on the first view; the player's own moves are never overridden. */
  private framed = false;
  private touched = false;
  /** The Signal's effects (lit relays, heartbeat, the Silence, orders landing): decoration only. */
  private sfx: SignalFx | null = null;
  private prevRelays: RelayView[] | null = null;
  /** Relays lit since the last view whose comet is still running: drawn as before until it arrives. */
  private litAnim = new Set<string>();
  private galaxyPx = 0;
  private seasonDays = 56;
  private silenceOverride: number | null = null;

  constructor(private readonly cb: MapCallbacks) {}

  async mount(el: HTMLElement): Promise<void> {
    await this.app.init({ resizeTo: el, background: 0x080c1c, antialias: true, resolution: Math.min(2, window.devicePixelRatio || 1), autoDensity: true });
    el.appendChild(this.app.canvas);
    this.tex = { glow: glowTexture(), core: coreTexture(), dot: dotTexture(), ring: ringTexture(), ship: shipTexture(), cargo: cargoTexture(), pulse: pulseTexture(), scout: scoutTexture() };
    const sfx = this.sfx = new SignalFx({ glow: this.tex.glow, dot: this.tex.dot, pulse: this.tex.pulse }, fxLevel());
    this.world.addChild(this.starfield, this.fogLayer, this.sectorLayer, this.lightLayer, this.nebulaLayer, this.holeLayer, sfx.silence, sfx.cover, this.relayGlow, this.relayLayer, this.pulseLayer, sfx.fx, this.systemLayer, this.fleetLayer, this.reticle, this.flashLayer, sfx.top, this.labelLayer);
    this.app.stage.addChild(this.world);
    this.world.scale.set(Math.min(0.5, Math.max(0.28, el.clientWidth / 2600)));
    this.world.position.set(el.clientWidth / 2, el.clientHeight / 2);
    this.bindCamera(this.app.canvas);
    this.app.ticker.add((tk) => this.animate(tk.deltaMS));
    this.ready = true;
    if (this.view) { this.render(this.view); this.frameNetwork(); }
  }

  /** Removes the canvas and frees this scene's GPU objects. `releaseGlobalResources` stays off: with `true` Pixi
   *  empties pools shared by every renderer on the page (the batch pool among them), and the other scene's next
   *  frame crashes on a destroyed batch, which left the galaxy black after leaving a system. */
  destroy(): void { this.app.destroy({ removeView: true, releaseGlobalResources: false }, { children: true }); }

  /** Three expanding rings on a star, over about 1.6 s. */
  flash(systemId: string): void {
    const sys = this.systemById.get(systemId);
    if (!sys || !this.ready) return;
    const g = new Graphics();
    this.flashLayer.addChild(g);
    this.flashes.push({ g, x: sys.x, y: sys.y, t: 0 });
  }

  setSelection(id: string | null): void { this.selected = id; if (this.view && this.ready) this.render(this.view); }
  setLinkFrom(id: string | null): void { this.linkFrom = id; if (this.view && this.ready) this.render(this.view); }

  centerOn(systemId: string): void {
    const s = this.systemById.get(systemId);
    if (!s) return;
    const to = this.centerOf(s);
    this.world.position.set(to.x, to.y);
  }

  /** Where the world must sit for this star to be in the middle of what the player sees (the HUD and the panel
   *  cover part of the canvas). */
  private centerOf(s: { x: number; y: number }): { x: number; y: number } {
    const k = this.world.scale.x;
    const i = this.cb.insets?.() ?? { top: 0, right: 0, bottom: 0, left: 0 };
    const cx = i.left + Math.max(0, this.app.screen.width - i.left - i.right) / 2;
    const cy = i.top + Math.max(0, this.app.screen.height - i.top - i.bottom) / 2;
    return { x: cx - s.x * k, y: cy - s.y * k };
  }

  /** The camera glides to a star (ease in-out) instead of jumping; `ms` 0 jumps. */
  flyTo(systemId: string, ms: number): void {
    const s = this.systemById.get(systemId);
    if (!s || !this.ready) return;
    const to = this.centerOf(s);
    if (ms <= 0) { this.world.position.set(to.x, to.y); this.tween = null; return; }
    this.tween = { fx: this.world.position.x, fy: this.world.position.y, tx: to.x, ty: to.y, t: 0, ms };
  }

  /** One big pulse of Signal along a path of stars (the new star to the capital), then `onDone`. */
  signalRun(path: readonly string[], ms: number, onDone: () => void): void {
    const pts = path.map((id) => this.systemById.get(id)).filter((s): s is SystemView => !!s).map((s) => ({ x: s.x, y: s.y }));
    this.stopRun();
    if (!this.ready || pts.length < 2) { onDone(); return; }
    const sprite = new Sprite(this.tex.pulse);
    sprite.anchor.set(0.5); sprite.width = 64; sprite.height = 64; sprite.tint = SIGNAL; sprite.blendMode = 'add';
    sprite.position.set(pts[0]!.x, pts[0]!.y);
    this.flashLayer.addChild(sprite);
    this.run = { sprite, pts, t: 0, ms, done: onDone };
  }

  /** A tap ends the moment: the camera lands, the pulse arrives at once. */
  finishMoment(): void {
    if (this.tween) { this.world.position.set(this.tween.tx, this.tween.ty); this.tween = null; }
    this.stopRun();
  }

  private stopRun(): void {
    const r = this.run;
    if (!r) return;
    this.run = null;
    r.sprite.destroy();
    r.done();
  }

  update(view: PlayerView): void {
    const first = this.view === null;
    const changes = relayChanges(this.prevRelays, view.relays, view.me.id);
    this.prevRelays = view.relays.map((r) => ({ ...r }));
    this.view = view;
    this.simTimeAtReceive = view.time;
    this.receivedAt = performance.now();
    this.factionOf = new Map(view.colonies.map((c) => [c.id, c.faction]));
    this.systemById = new Map(view.systems.map((s) => [s.id, s]));
    if (!this.ready) return;
    if (first) this.buildStarfield(view.galaxyRadius);
    const sfx = this.sfx;
    if (sfx?.on) for (const c of changes.lit) this.litAnim.add(c.id);
    this.render(view);
    if (!this.framed) this.frameNetwork();
    if (!sfx?.on) return;
    const depth = networkDepth(view.relays, view.me.capital, view.me.id);
    for (const c of changes.lit) {
      const { from, to } = sourceEnd(c, depth);
      const a = this.systemById.get(from), b = this.systemById.get(to);
      if (!a || !b) { this.litAnim.delete(c.id); continue; }
      sfx.lightRelay({ x: a.x, y: a.y }, { x: b.x, y: b.y }, to, () => { this.litAnim.delete(c.id); if (this.view) { this.drawRelays(this.view); this.coverAndBeat(this.view); } });
    }
    for (const c of changes.started) {
      const a = this.systemById.get(c.a), b = this.systemById.get(c.b);
      if (a && b) sfx.startRelay({ x: a.x, y: a.y }, { x: b.x, y: b.y });
    }
  }

  /** The season's length in days (public config), for the Silence's front. */
  setSeasonDays(days: number): void { if (days > 0) this.seasonDays = days; if (this.view && this.ready) this.placeSilence(this.view); }
  /** Debug and capture: puts the Silence's front at this season progress (0..1); null gives it back to the clock. */
  setSilence(progress: number | null): void { this.silenceOverride = progress; if (this.view && this.ready) this.placeSilence(this.view); }

  /** An order landed on this star (the decision's trail arrives): the lock-on. */
  ack(systemId: string): void {
    const s = this.systemById.get(systemId);
    if (s && this.ready) this.sfx?.ack({ x: s.x, y: s.y });
  }

  /** Where a star is on the page (client pixels), or null when it is not on the visible canvas. */
  screenOf(systemId: string): { x: number; y: number } | null {
    const s = this.systemById.get(systemId);
    if (!s || !this.ready) return null;
    const rect = this.app.canvas.getBoundingClientRect();
    if (!rect.width) return null;
    const k = this.world.scale.x;
    const x = rect.left + this.world.position.x + s.x * k, y = rect.top + this.world.position.y + s.y * k;
    return x < rect.left - 40 || x > rect.right + 40 || y < rect.top - 40 || y > rect.bottom + 40 ? null : { x, y };
  }

  /** Debug and capture: puts the world point (x, y) in the middle of the visible map at scale k. */
  lookAt(x: number, y: number, k: number): void { this.touched = true; this.tween = null; this.world.scale.set(k); const c = this.centerOf({ x, y }); this.world.position.set(c.x, c.y); }
  /** Debug and capture: the network's heartbeat now. */
  beatNow(): void { this.sfx?.beatNow(); }
  /** Debug and capture: sets the zoom around the visible centre. */
  zoomTo(k: number): void { const r = this.app.canvas.getBoundingClientRect(); this.touched = true; this.zoomAt(r.left + r.width / 2, r.top + r.height / 2, k / this.world.scale.x); }

  /** Debug and capture: replays the lighting of a relay of mine (a → b) as if it had just lit. */
  replayLight(a: string, b: string): void {
    const v = this.view, sfx = this.sfx;
    if (!v || !sfx) return;
    const r = v.relays.find((x) => (x.a === a && x.b === b) || (x.a === b && x.b === a));
    const A = this.systemById.get(a), B = this.systemById.get(b);
    if (!A || !B) return;
    if (r) { this.litAnim.add(r.id); this.drawRelays(v); }
    sfx.lightRelay({ x: A.x, y: A.y }, { x: B.x, y: B.y }, b, () => { if (r) this.litAnim.delete(r.id); if (this.view) { this.drawRelays(this.view); this.coverAndBeat(this.view); } });
  }

  private coverAndBeat(v: PlayerView): void {
    const sfx = this.sfx;
    if (!sfx?.on) return;
    const live = v.relays.filter((r) => !this.litAnim.has(r.id));
    const depth = networkDepth(live, v.me.capital, v.me.id);
    const pts = new Map<string, { x: number; y: number }>();
    for (const id of depth.keys()) { const s = this.systemById.get(id); if (s) pts.set(id, { x: s.x, y: s.y }); }
    sfx.setCover(pts);
    const edges: { from: { x: number; y: number }; to: { x: number; y: number }; depth: number }[] = [];
    for (const r of live) {
      if (r.owner !== v.me.id || !r.ready || r.cut) continue;
      const da = depth.get(r.a), db = depth.get(r.b);
      if (da === undefined || db === undefined || da === db) continue;
      const [f, t] = da < db ? [r.a, r.b] : [r.b, r.a];
      const F = this.systemById.get(f), T = this.systemById.get(t);
      if (F && T) edges.push({ from: { x: F.x, y: F.y }, to: { x: T.x, y: T.y }, depth: Math.min(da, db) });
    }
    const cap = this.systemById.get(v.me.capital);
    sfx.setBeat(edges, [...depth].map(([id, d]) => ({ p: pts.get(id)!, depth: d })).filter((n) => n.p), cap ? { x: cap.x, y: cap.y } : null);
  }

  private placeSilence(v: PlayerView): void {
    if (!this.sfx) return;
    if (!this.galaxyPx) for (const h of hexDisk(v.galaxyRadius)) { const c = hexToPixel(h, HEX_SIZE); this.galaxyPx = Math.max(this.galaxyPx, Math.hypot(c.x, c.y) + HEX_SIZE); }
    const p = this.silenceOverride ?? seasonProgress(v.time, v.seasonEndsAt, this.seasonDays);
    this.sfx.setSilence(silenceFront(p, this.galaxyPx), this.galaxyPx, this.starfield.children as Sprite[]);
  }

  /** First login: the capital in the middle of the visible map, its network and the stars in range around it. */
  frameNetwork(): void {
    const v = this.view, f = this.focus;
    if (!v || !f || !this.ready || this.touched) return;
    const ids = [f.capital, ...f.mine, ...f.inRange];
    const pts = ids.map((id) => this.systemById.get(id)).filter((s): s is SystemView => !!s);
    if (!pts.length) return;
    const insets = this.cb.insets?.() ?? { top: 0, right: 0, bottom: 0, left: 0 };
    const fr = frameOn(pts, this.app.screen, insets, { min: 0.28, max: 0.85, pad: 140 });
    if (!fr) return;
    this.world.scale.set(fr.scale);
    this.world.position.set(fr.x, fr.y);
    this.framed = true;
    this.render(v); // relay widths follow the scale
  }

  /** Simulated time right now, extrapolated between server pushes (the server runs 1:1 or a known scale). */
  private simNow(): number {
    return this.simTimeAtReceive + ((performance.now() - this.receivedAt) / 1000) * (this.view?.timeScale ?? 1);
  }

  // --- rendering -----------------------------------------------------------

  private render(v: PlayerView): void {
    this.focus = networkFocus(v);
    this.drawFog(v);
    this.drawSectors(v);
    this.drawRelays(v);
    this.drawSystems(v);
    this.drawFleets(v);
    this.coverAndBeat(v);
    this.placeSilence(v);
    this.lastLabelScale = -1;
  }

  private buildStarfield(radius: number): void {
    this.starfield.removeChildren();
    const extent = (radius + 2) * HEX_SIZE * 1.6;
    let seed = 1234567;
    const rnd = (): number => { seed = (seed * 1664525 + 1013904223) >>> 0; return seed / 4294967296; };
    // Dense enough that no part of the galaxy reads as a black tile (issue #44).
    for (let i = 0; i < 2400; i++) {
      const sp = new Sprite(this.tex.dot);
      sp.anchor.set(0.5);
      sp.position.set((rnd() * 2 - 1) * extent, (rnd() * 2 - 1) * extent);
      const size = 3 + rnd() * rnd() * 14;
      sp.width = size; sp.height = size;
      sp.alpha = 0.4 + rnd() * 0.6;
      sp.tint = [0xffffff, 0xcfe4ff, 0xffe6c7, 0xd8d0ff][Math.floor(rnd() * 4)]!;
      this.starfield.addChild(sp);
    }
  }

  private drawFog(v: PlayerView): void {
    const g = this.fogLayer;
    g.clear();
    const visible = new Set(v.sectors.map((s) => s.key));
    const px = 1 / Math.max(0.2, this.world.scale.x);
    for (const h of hexDisk(v.galaxyRadius)) {
      const key = `${h.q},${h.r}`;
      if (visible.has(key)) continue;
      const c = hexToPixel(h, HEX_SIZE);
      // Unknown space recedes (issue #37), but the starfield shows through it and, around the player, so does the
      // neighbourhood's light (issue #39); the grid is a one-pixel ghost, all but gone away from the network.
      // The veil is a deep blue, not black, and thinner (issue #44): unknown space still reads as space.
      const near = this.tierOf(key) !== 'far';
      hexPath(g, c.x, c.y, HEX_SIZE * 0.985);
      g.fill({ color: 0x0b1128, alpha: near ? 0.4 : 0.55 });
      g.stroke({ color: 0x24305a, width: px, alpha: near ? 0.14 : 0.07 });
    }
  }

  private drawSectors(v: PlayerView): void {
    const g = this.sectorLayer;
    g.clear();
    this.nebulaLayer.removeChildren();
    this.holeLayer.removeChildren();
    this.rings = [];
    this.drawLight(v.galaxyRadius);
    const px = 1 / Math.max(0.2, this.world.scale.x);
    for (const s of v.sectors) {
      const c = hexToPixel({ q: s.q, r: s.r }, HEX_SIZE);
      // The neighbourhood reads as space, not as tiles (issue #39): a thin veil and a faint edge; far sectors are
      // almost only their stars.
      const tier = this.tierOf(s.key);
      hexPath(g, c.x, c.y, HEX_SIZE * 0.985);
      g.fill({ color: tier === 'home' ? 0x101c3a : 0x0a1022, alpha: tier === 'home' ? 0.32 : tier === 'near' ? 0.26 : 0.3 });
      g.stroke({ color: tier === 'home' ? 0x3a4f8a : 0x243056, width: (tier === 'home' ? 1.5 : 1) * px, alpha: tier === 'home' ? 0.32 : tier === 'near' ? 0.16 : 0.06 });
      const nebAlpha = tier === 'far' ? 0.42 : 0.5;
      s.nebulae.forEach((n, i) => {
        const hue = (s.q * 53 + s.r * 97 + i * 41) % 3;
        const tint = [0x5a4bd6, 0x2a8f9d, 0x8a46c9][hue]!;
        for (let k = 0; k < 2; k++) {
          const sp = new Sprite(nebulaTexture(1 + ((s.q + 20) * 31 + (s.r + 20) * 7 + i * 3 + k) % 9));
          sp.anchor.set(0.5);
          sp.position.set(n.x + (k ? n.r * 0.2 : 0), n.y - (k ? n.r * 0.15 : 0));
          sp.width = n.r * 2.7; sp.height = n.r * 2.7;
          sp.rotation = (s.q + i + k) * 0.7;
          sp.tint = tint;
          sp.alpha = nebAlpha;
          sp.blendMode = 'add';
          this.nebulaLayer.addChild(sp);
        }
      });
      for (const b of s.blackHoles) {
        const ring = new Sprite(this.tex.ring);
        ring.anchor.set(0.5); ring.position.set(b.x, b.y);
        ring.width = b.r * 5.2; ring.height = b.r * 5.2; ring.tint = 0xffa060; ring.alpha = 0.85; ring.blendMode = 'add';
        ring.rotation = (b.x * 0.01) % Math.PI;
        this.holeLayer.addChild(ring);
        this.rings.push(ring);
        const halo = new Sprite(this.tex.glow);
        halo.anchor.set(0.5); halo.position.set(b.x, b.y); halo.width = b.r * 6; halo.height = b.r * 6; halo.tint = 0x000000; halo.alpha = 0.9;
        this.holeLayer.addChild(halo);
        const core = new Graphics(); core.circle(b.x, b.y, b.r); core.fill({ color: 0x000000 }); core.stroke({ color: 0xffb27a, width: 2, alpha: 0.5 });
        this.holeLayer.addChild(core);
      }
    }
  }

  private tierOf(key: string): SectorTier { return this.focus ? sectorTier(this.focus, key) : 'far'; }

  /** A living galaxy around the player (issue #39): each home sector and each one around it carries a wide wash of
   *  nebula light (cached textures, additive, static; nothing animates per frame), brighter at home. */
  private drawLight(galaxyRadius: number): void {
    this.lightLayer.removeChildren();
    const f = this.focus;
    if (!f) return;
    // A faint haze over the whole disk (issue #44): far sectors are dim, never black. One static wash per hex,
    // seeded by the hex, drawn under the neighbourhood's own light.
    for (const h of hexDisk(galaxyRadius)) {
      const key = `${h.q},${h.r}`;
      if (f.homeSectors.has(key) || f.nearSectors.has(key)) continue;
      const c = hexToPixel(h, HEX_SIZE);
      const sp = new Sprite(nebulaTexture(1 + (((h.q + 20) * 17 + (h.r + 20) * 23) % 9)));
      sp.anchor.set(0.5);
      sp.position.set(c.x + HEX_SIZE * 0.1 * ((h.q + h.r) % 3 - 1), c.y + HEX_SIZE * 0.1 * ((h.q - h.r) % 3));
      sp.width = sp.height = HEX_SIZE * 2.3;
      sp.rotation = (h.q * 2 + h.r) * 0.8;
      sp.tint = [0x3a4bb6, 0x1f6f8d, 0x5a3a9c][Math.abs(h.q * 5 + h.r * 7) % 3]!;
      sp.alpha = 0.11;
      sp.blendMode = 'add';
      this.lightLayer.addChild(sp);
    }
    const lit: [string, SectorTier][] = [...[...f.homeSectors].map((k) => [k, 'home'] as [string, SectorTier]), ...[...f.nearSectors].map((k) => [k, 'near'] as [string, SectorTier])];
    for (const [key, tier] of lit) {
      const [q, r] = key.split(',').map(Number) as [number, number];
      const c = hexToPixel({ q, r }, HEX_SIZE);
      const home = tier === 'home';
      // A deep glow under the sector, then two noisy washes in the art direction's blues and violets; the ring
      // around home is lit for real (issue #44), so the player's neighbourhood reads before it is explored.
      const glow = new Sprite(this.tex.glow);
      glow.anchor.set(0.5); glow.position.set(c.x, c.y); glow.width = glow.height = HEX_SIZE * (home ? 3.4 : 3.0);
      glow.tint = home ? 0x23357a : 0x1b2660; glow.alpha = home ? 0.36 : 0.3; glow.blendMode = 'add';
      this.lightLayer.addChild(glow);
      for (let k = 0; k < 2; k++) {
        const sp = new Sprite(nebulaTexture(1 + (((q + 20) * 13 + (r + 20) * 29 + k * 5) % 9)));
        sp.anchor.set(0.5);
        sp.position.set(c.x + (k ? HEX_SIZE * 0.25 : -HEX_SIZE * 0.15), c.y + (k ? -HEX_SIZE * 0.2 : HEX_SIZE * 0.1));
        sp.width = sp.height = HEX_SIZE * (home ? 2.6 : 2.4);
        sp.rotation = (q * 3 + r + k) * 0.9;
        sp.tint = [0x4a5bd6, 0x2a7f9d, 0x7a46c9][(Math.abs(q * 5 + r * 7) + k) % 3]!;
        sp.alpha = home ? 0.32 : 0.26;
        sp.blendMode = 'add';
        this.lightLayer.addChild(sp);
      }
      // Dust of faint stars in the lit sectors (seeded by the sector, so it never shimmers between views).
      let seed = ((q + 64) * 7919 + (r + 64) * 104729) >>> 0;
      const rnd = (): number => { seed = (seed * 1664525 + 1013904223) >>> 0; return seed / 4294967296; };
      for (let i = 0, n = home ? 36 : 28; i < n; i++) {
        const a = rnd() * Math.PI * 2, d = Math.sqrt(rnd()) * HEX_SIZE * 0.9;
        const dot = new Sprite(this.tex.dot);
        dot.anchor.set(0.5); dot.position.set(c.x + Math.cos(a) * d, c.y + Math.sin(a) * d);
        dot.width = dot.height = 4 + rnd() * rnd() * 10;
        dot.alpha = (home ? 0.35 : 0.25) + rnd() * 0.4;
        dot.tint = [0xffffff, 0xcfe4ff, 0xffe6c7, 0xd8d0ff][Math.floor(rnd() * 4)]!;
        this.lightLayer.addChild(dot);
      }
    }
  }

  private drawRelays(v: PlayerView): void {
    const glow = this.relayGlow, g = this.relayLayer;
    glow.clear(); g.clear();
    // Widths are meant in screen pixels: divide by the world scale so relays stay readable on a phone.
    const px = 1 / Math.max(0.2, this.world.scale.x);
    this.pulseLayer.removeChildren();
    this.pulses = [];
    for (const rt of this.relayTimers) rt.text.destroy();
    this.relayTimers = [];
    for (const r of v.relays) {
      const a = this.systemById.get(r.a), b = this.systemById.get(r.b);
      if (!a || !b) continue;
      const mine = r.owner === v.me.id;
      const color = mine ? SIGNAL : FACTION_COLOR[this.factionOf.get(r.owner) ?? ''] ?? 0x8899aa;
      if (r.cut) { dashed(g, a.x, a.y, b.x, b.y, 16, 12, { color: DANGER, width: 2.5 * px, alpha: 0.9 }); continue; }
      // Lit since the last view, the Signal still on its way: the dashes stay until the comet arrives (signalFx).
      if (this.litAnim.has(r.id)) { dashed(g, a.x, a.y, b.x, b.y, 10, 8, { color, width: 3 * px, alpha: 0.5 }); continue; }
      // Mine under construction: a bright dashed line with a faint glow, so the player sees it is coming.
      if (!r.ready) {
        if (mine) glow.moveTo(a.x, a.y).lineTo(b.x, b.y).stroke({ color, width: 10 * px, alpha: 0.1 });
        dashed(g, a.x, a.y, b.x, b.y, mine ? 10 : 6, mine ? 8 : 10, { color, width: (mine ? 3 : 2) * px, alpha: mine ? 0.95 : 0.6 });
        if (mine) {
          const text = new Text({ text: '', style: TIMER_STYLE });
          text.anchor.set(0.5); text.position.set((a.x + b.x) / 2, (a.y + b.y) / 2);
          this.pulseLayer.addChild(text);
          this.relayTimers.push({ text, readyAt: r.readyAt });
        }
        continue;
      }
      // My network is the brightest thing on the map: a wide soft glow under a thick line.
      glow.moveTo(a.x, a.y).lineTo(b.x, b.y).stroke({ color: r.bridge && mine ? BRIDGE : color, width: (mine ? 16 : 5) * px, alpha: mine ? 0.26 : 0.08 });
      g.moveTo(a.x, a.y).lineTo(b.x, b.y).stroke({ color, width: (mine ? 4 : 1.5) * px, alpha: mine ? 1 : 0.6 });
      if (mine || this.isAlly(v, r.owner)) this.addPulses(r, a, b, color);
    }
    if (this.linkFrom && this.selected && this.linkFrom !== this.selected) {
      const a = this.systemById.get(this.linkFrom), b = this.systemById.get(this.selected);
      if (a && b) dashed(g, a.x, a.y, b.x, b.y, 12, 10, { color: 0xffffff, width: 2 * px, alpha: 0.85 });
    }
  }

  private isAlly(v: PlayerView, colonyId: string): boolean {
    return v.colonies.find((c) => c.id === colonyId)?.ally ?? false;
  }

  private addPulses(r: RelayView, a: SystemView, b: SystemView, color: number): void {
    const len = Math.hypot(b.x - a.x, b.y - a.y);
    const count = Math.max(1, Math.round(len / 220));
    for (let i = 0; i < count; i++) {
      const sp = new Sprite(this.tex.pulse);
      sp.anchor.set(0.5); sp.width = 22; sp.height = 22; sp.tint = color; sp.alpha = 0.9; sp.blendMode = 'add';
      this.pulseLayer.addChild(sp);
      this.pulses.push({ sprite: sp, ax: a.x, ay: a.y, bx: b.x, by: b.y, t: (i / count + (r.id.length % 7) / 7) % 1, speed: 0.00035 * (220 / Math.max(120, len)) });
    }
  }

  private drawSystems(v: PlayerView): void {
    this.systemLayer.removeChildren();
    this.labelLayer.removeChildren();
    this.engagedRings = [];
    this.capitalPulse = null;
    const focus = this.focus ?? networkFocus(v);
    const capStyle = new TextStyle({ fill: 0xffd98a, fontSize: 40, fontFamily: 'Rajdhani, system-ui, sans-serif', fontWeight: '700', letterSpacing: 2, stroke: { color: 0x05070f, width: 7 } });
    const rangeStyle = new TextStyle({ fill: 0xa9dcf5, fontSize: 26, fontFamily: 'Rajdhani, system-ui, sans-serif', fontWeight: '600', stroke: { color: 0x05070f, width: 5 } });
    const style = new TextStyle({ fill: 0xe6ecf7, fontSize: 34, fontFamily: 'Rajdhani, system-ui, sans-serif', fontWeight: '600', letterSpacing: 1, stroke: { color: 0x05070f, width: 6 } });
    const small = new TextStyle({ fill: 0x9fb0d0, fontSize: 26, fontFamily: 'Rajdhani, system-ui, sans-serif', stroke: { color: 0x05070f, width: 5 } });
    for (const s of v.systems) {
      const node = new Container();
      node.position.set(s.x, s.y);
      const base = 10 + s.slots * 3;
      const color = RESOURCE_COLOR[s.resource] ?? 0xffffff;
      const owned = !!s.owner;
      const mine = s.owner === v.me.id;
      const capital = mine && s.id === v.me.capital;
      const next = !this.linkFrom && focus.inRange.has(s.id);
      // A neutral star in the neighbourhood (home sector or the ring around it) stands out: the player's surroundings
      // read as a place, not as dust (issue #39).
      const nearby = !owned && !next && s.kind !== 'beacon' && sectorTier(focus, s.sector) !== 'far';

      // The capital: a wide amber glow under everything, the first thing the eye finds (issue #37).
      if (capital) {
        const cg = new Sprite(this.tex.glow);
        cg.anchor.set(0.5); cg.tint = CAPITAL; cg.blendMode = 'add'; cg.width = cg.height = base * 16; cg.alpha = 0.55;
        node.addChild(cg);
      }
      // Connected systems breathe with the Signal; others are quiet.
      const halo = new Sprite(this.tex.glow);
      halo.anchor.set(0.5); halo.tint = capital ? CAPITAL : s.connected ? SIGNAL : color; halo.blendMode = 'add';
      halo.width = halo.height = base * (s.connected ? 9 : nearby ? 7 : 5); halo.alpha = s.connected ? (mine ? 0.7 : 0.55) : nearby ? 0.5 : 0.28;
      node.addChild(halo);
      // Stars one relay away: a soft halo and a thin ring, the next moves.
      if (next) {
        const nh = new Sprite(this.tex.glow);
        nh.anchor.set(0.5); nh.tint = SIGNAL; nh.blendMode = 'add'; nh.width = nh.height = base * 8; nh.alpha = 0.3;
        node.addChildAt(nh, 0);
        const nr = new Graphics(); nr.circle(0, 0, base + 14); nr.stroke({ color: SIGNAL, width: 2, alpha: 0.5 });
        node.addChild(nr);
      }

      if (s.kind === 'beacon') {
        const star = new Graphics();
        star.star(0, 0, 7, base * 2.6, base * 1.3, 0);
        star.fill({ color: s.lit ? 0xffd166 : 0x39456b, alpha: s.lit ? 0.95 : 0.8 });
        star.stroke({ color: s.lit ? 0xfff2c2 : 0x5a6a9a, width: 2 });
        node.addChild(star);
        if (s.lit) { const bl = new Sprite(this.tex.glow); bl.anchor.set(0.5); bl.tint = 0xffd166; bl.blendMode = 'add'; bl.width = bl.height = base * 14; bl.alpha = 0.5; node.addChildAt(bl, 0); }
        // Decision 0020 (expeditions on): the lit Beacon's reach (range x1.3 within two sectors) is drawn, and a Beacon with an
        // expedition under way breathes. Only when the rule is on, so the map of a season without it is unchanged.
        if (v.me.phares && s.byExpedition) {
          if (s.lit) {
            const zone = new Graphics(), zr = SECTOR_SIZE * 2.6, zpx = 1 / Math.max(0.1, this.world.scale.x);
            zone.circle(0, 0, zr); zone.fill({ color: 0xffd166, alpha: 0.07 });
            for (let i = 0; i < 48; i++) { const a0 = (i / 48) * Math.PI * 2; zone.arc(0, 0, zr, a0, a0 + 0.07); zone.stroke({ color: 0xffd166, width: 4 * zpx, alpha: 0.8 }); }
            node.addChildAt(zone, 0);
          } else if (v.me.phares.trial?.kind === 'beacon' && v.me.phares.trial.system === s.id) {
            const tr = new Graphics(); tr.circle(0, 0, base * 3.4); tr.stroke({ color: 0xffd166, width: 3, alpha: 0.9 }); node.addChild(tr); this.engagedRings.push(tr);
          }
        }
      }
      // Decision 0020: an ancient relay found or woken in this system, a gold diamond (lit when woken).
      if (s.ancient) {
        const woken = s.ancient.woken.length > 0, d = base * 0.9, ag = new Graphics();
        ag.moveTo(0, -d).lineTo(d * 0.8, 0).lineTo(0, d).lineTo(-d * 0.8, 0).closePath();
        ag.fill({ color: 0xffd166, alpha: woken ? 0.95 : 0.3 }); ag.stroke({ color: 0xfff2c2, width: 2, alpha: 0.95 });
        ag.position.set(-base - 16, -base - 12);
        node.addChild(ag);
        if (woken) { const ab = new Sprite(this.tex.glow); ab.anchor.set(0.5); ab.tint = 0xffd166; ab.blendMode = 'add'; ab.alpha = 0.6; ab.width = ab.height = d * 5; ab.position.copyFrom(ag.position); node.addChildAt(ab, 0); }
      }
      if (s.kind === 'pulsar') {
        const rays = new Graphics();
        for (let i = 0; i < 2; i++) { const a = i * Math.PI / 2 + 0.5; rays.moveTo(Math.cos(a) * base * 1.4, Math.sin(a) * base * 1.4).lineTo(Math.cos(a) * base * 3.2, Math.sin(a) * base * 3.2); rays.moveTo(-Math.cos(a) * base * 1.4, -Math.sin(a) * base * 1.4).lineTo(-Math.cos(a) * base * 3.2, -Math.sin(a) * base * 3.2); }
        rays.stroke({ color: 0xffffff, width: 2.5, alpha: 0.85 });
        node.addChild(rays);
      }

      const core = new Sprite(this.tex.core);
      core.anchor.set(0.5); core.width = core.height = base * (nearby ? 2.7 : 2.3); core.tint = STAR_TINT[s.resource] ?? 0xffffff;
      node.addChild(core);
      const inner = new Sprite(this.tex.core);
      inner.anchor.set(0.5); inner.width = inner.height = base * (nearby ? 1.5 : 1.3); inner.tint = color;
      node.addChild(inner);

      if (owned) {
        const fc = FACTION_COLOR[this.factionOf.get(s.owner!) ?? ''] ?? 0xffffff;
        const ring = new Graphics();
        ring.circle(0, 0, base + 9); ring.stroke({ color: capital ? CAPITAL : fc, width: mine ? 4 : 2.5, alpha: 0.95 });
        if (capital) {
          ring.circle(0, 0, base + 17); ring.stroke({ color: CAPITAL, width: 2.5, alpha: 0.8 });
          const beat = new Graphics(); beat.circle(0, 0, base + 30); beat.stroke({ color: CAPITAL, width: 3, alpha: 1 });
          node.addChild(beat);
          this.capitalPulse = beat;
        } else if (mine && !s.connected) {
          // Mine, waiting for its relay: a dashed Signal ring.
          for (let i = 0; i < 10; i++) { const a0 = (i / 10) * Math.PI * 2; ring.arc(0, 0, base + 16, a0, a0 + 0.35); ring.stroke({ color: SIGNAL, width: 2, alpha: 0.8 }); }
        }
        node.addChild(ring);
      }
      if (s.blockadedBy) { const bl = new Graphics(); bl.circle(0, 0, base + 20); bl.stroke({ color: DANGER, width: 4, alpha: 0.9 }); node.addChild(bl); }
      if (s.engaged) { const eg = new Graphics(); eg.circle(0, 0, base + 26); eg.stroke({ color: DANGER, width: 3, alpha: 0.9 }); node.addChild(eg); this.engagedRings.push(eg); }
      if (s.signature) { // someone lives here under cover: a dashed ring and a question mark
        const sg = new Graphics(); for (let i = 0; i < 12; i++) { const a0 = (i / 12) * Math.PI * 2; sg.arc(0, 0, base + 9, a0, a0 + 0.3); sg.stroke({ color: 0xffb060, width: 2.5, alpha: 0.9 }); } node.addChild(sg);
        const q = new Text({ text: '?', style: new TextStyle({ fill: 0xffb060, fontSize: 26, fontFamily: 'Rajdhani, system-ui, sans-serif', fontWeight: '700', stroke: { color: 0x05070f, width: 5 } }) });
        q.anchor.set(0.5); q.position.set(-base - 14, -base - 10); node.addChild(q);
      }

      // Resource glyph, so the map reads without a legend.
      const glyph = new Sprite(glyphTexture(s.resource));
      glyph.anchor.set(0.5); glyph.width = glyph.height = 20; glyph.tint = color; glyph.alpha = 0.95;
      glyph.position.set(base + 14, base + 10);
      const badge = new Graphics(); badge.circle(base + 14, base + 10, 13); badge.fill({ color: 0x05070f, alpha: 0.85 }); badge.stroke({ color, width: 1.5, alpha: 0.8 });
      node.addChild(badge, glyph);

      if (s.id === this.linkFrom) { const lf = new Graphics(); lf.circle(0, 0, base + 30); lf.stroke({ color: SIGNAL, width: 3 }); node.addChild(lf); }
      if (this.linkFrom && this.linkFrom !== s.id) {
        const cand = v.linkTargets[this.linkFrom]?.find((c) => c.to === s.id);
        if (cand) {
          const ok = new Graphics(); ok.circle(0, 0, base + 22); ok.stroke({ color: 0x7ee2a8, width: 3, alpha: 0.95 }); node.addChild(ok);
          const cost = new Text({ text: `${cand.metal}⬡ ${cand.energy}⚡`, style: new TextStyle({ fill: 0x7ee2a8, fontSize: 24, fontFamily: 'Rajdhani, system-ui, sans-serif', fontWeight: '600', stroke: { color: 0x05070f, width: 5 } }) });
          cost.anchor.set(0.5, 1); cost.position.set(0, -base - 24); node.addChild(cost);
        } else {
          node.alpha = 0.45;
        }
      } else if (!owned && !next && s.kind !== 'beacon' && !nearby) {
        node.alpha = 0.7; // neutral stars far from the network step back behind it
      }

      // An ancient wreck to explore (Nick 12.10: « je ne vois pas d'épave »): a broken hull mark, lower left.
      if (s.wreck && expeditionsOn.value) {
        const wk = new Graphics();
        // QA 12.10: ~5 px and dull at 390, lost in the capital's orange ring — bigger, brighter, dark-rimmed.
        wk.poly([-11, 3, 0, -6, 11, 3, 5, 3, 0, 0, -5, 3]);
        wk.fill({ color: 0xffb066, alpha: 1 });
        wk.stroke({ color: 0x1a0f05, width: 2, alpha: 0.95 });
        wk.scale.set(1.35);
        // Outside the capital's rings (base + 14): QA 12.10 found it inside the orange one.
        wk.position.set(-(base + 22), base + 14);
        node.addChild(wk);
      }
      // An Echo's shard lying here (decision 0015 § 2.D): a small crystal by the star — violet mine, red another's.
      const shards = (v.shards ?? []).filter((x) => x.system === s.id);
      if (shards.length) {
        const g = new Graphics();
        g.poly([0, -8, 6, 0, 0, 8, -6, 0]);
        g.fill({ color: shards.some((x) => x.mine) ? 0xc58cff : 0xff8c8c, alpha: 0.95 });
        g.stroke({ color: 0xffffff, width: 1.5, alpha: 0.8 });
        // Upper left: docked fleets start upper right (-π/4) and go clockwise (QA 12.10: the crystal hid under the first).
        g.position.set(-(base + 12), -(base + 12));
        node.addChild(g);
      }
      node.eventMode = 'static';
      node.cursor = 'pointer';
      node.hitArea = { contains: (x: number, y: number) => Math.hypot(x, y) <= base + 34 };
      node.on('pointertap', () => { if (!this.dragMoved) this.cb.onSelect(s.id); });
      this.systemLayer.addChild(node);

      // My systems and the capital are always labelled; stars in range read from mid zoom.
      const label = new Text({ text: capital ? `★ ${s.name}` : s.name, style: capital ? capStyle : mine || s.kind === 'beacon' || s.id === this.selected ? style : next ? rangeStyle : small });
      label.anchor.set(0.5, 0);
      label.position.set(s.x, s.y + base + (capital ? 34 : 24));
      (label as Text & { lod: number }).lod = mine || s.kind === 'beacon' || s.id === this.selected ? 0 : next ? 0.3 : owned || nearby ? 0.4 : 0.6;
      this.labelLayer.addChild(label);
    }
  }

  /** Known wormholes: a swirl at each mouth and a faint dotted arc between them (fleets only, no relay). */
  private drawWormholes(v: PlayerView): void {
    this.swirls = [];
    const px = 1 / Math.max(0.2, this.world.scale.x);
    for (const h of v.me.wormholes ?? []) {
      const a = this.systemById.get(h.a), b = this.systemById.get(h.b);
      if (!a || !b) continue;
      const arc = new Graphics();
      const mx = (a.x + b.x) / 2, my = (a.y + b.y) / 2, dx = b.x - a.x, dy = b.y - a.y, len = Math.hypot(dx, dy) || 1;
      const cx = mx - (dy / len) * len * 0.18, cy = my + (dx / len) * len * 0.18;
      const n = 40;
      for (let i = 0; i < n; i += 2) {
        const p = (t: number): [number, number] => [(1 - t) ** 2 * a.x + 2 * (1 - t) * t * cx + t * t * b.x, (1 - t) ** 2 * a.y + 2 * (1 - t) * t * cy + t * t * b.y];
        const [x0, y0] = p(i / n), [x1, y1] = p((i + 1) / n);
        arc.moveTo(x0, y0).lineTo(x1, y1);
      }
      arc.stroke({ color: WORMHOLE, width: 2.5 * px, alpha: 0.55 });
      this.fleetLayer.addChild(arc);
      for (const s of [a, b]) {
        const g = new Graphics();
        for (let i = 0; i < 3; i++) { g.arc(0, 0, 34 + i * 7, i * 2.1, i * 2.1 + 3.6); g.stroke({ color: WORMHOLE, width: 3 - i * 0.6, alpha: 0.9 - i * 0.2 }); }
        g.position.set(s.x, s.y);
        this.fleetLayer.addChild(g);
        this.swirls.push(g);
      }
    }
  }

  private drawFleets(v: PlayerView): void {
    this.fleetLayer.removeChildren();
    this.ships = [];
    this.drawWormholes(v);
    let parked = new Map<string, number>();
    for (const f of v.fleets) {
      const mine = f.owner === v.me.id;
      const scout = f.scout;
      const convoy = !scout && (f.order === 'convoy' || (f.combat === 0 && f.size > 0));
      const color = mine ? (scout ? SIGNAL : convoy ? 0xbfefff : 0xffffff) : FACTION_COLOR[this.factionOf.get(f.owner) ?? ''] ?? DANGER;
      const sp = new Sprite(scout ? this.tex.scout : convoy ? this.tex.cargo : this.tex.ship);
      sp.anchor.set(0.5); sp.tint = color; sp.width = sp.height = scout ? 52 : (convoy ? 34 : 44) + Math.min(30, f.combat || f.size);
      if (scout) sp.blendMode = 'add';
      if (f.at) {
        const s = this.systemById.get(f.at);
        if (!s) continue;
        const n = parked.get(f.at) ?? 0; parked.set(f.at, n + 1);
        const a = -Math.PI / 4 + n * 0.5;
        sp.position.set(s.x + Math.cos(a) * 48, s.y + Math.sin(a) * 48); sp.rotation = a + Math.PI;
        this.fleetLayer.addChild(sp);
        if (f.order === 'blockade' || f.order === 'raid' || f.order === 'ambush') { const ring = new Graphics(); ring.circle(s.x, s.y, 46); ring.stroke({ color: DANGER, width: 2, alpha: 0.8 }); this.fleetLayer.addChild(ring); }
      } else {
        const from = f.from ? this.systemById.get(f.from) : undefined;
        const to = f.destination ? this.systemById.get(f.destination) : undefined;
        if (!to) continue;
        const fx = from?.x ?? to.x, fy = from?.y ?? to.y;
        sp.rotation = Math.atan2(to.y - fy, to.x - fx);
        this.fleetLayer.addChild(sp);
        const trail = new Graphics(); dashed(trail, fx, fy, to.x, to.y, 6, 14, { color, width: 1.5, alpha: 0.35 });
        // A convoy's remaining hops, so a player sees where it can be caught.
        if (convoy && f.path.length) {
          let lx = to.x, ly = to.y;
          for (const wp of f.path) { const s = this.systemById.get(wp); if (!s) break; dashed(trail, lx, ly, s.x, s.y, 4, 16, { color, width: 1.2, alpha: 0.22 }); lx = s.x; ly = s.y; }
        }
        this.fleetLayer.addChildAt(trail, 0);
        this.ships.push({ sprite: sp, fromX: fx, fromY: fy, toX: to.x, toY: to.y, departAt: f.departAt, arriveAt: f.arriveAt });
      }
    }
    parked = new Map();
  }

  private animate(dtMs: number): void {
    if (!this.view) return;
    if (this.tween) {
      const tw = this.tween;
      tw.t = Math.min(1, tw.t + dtMs / tw.ms);
      const e = tw.t < 0.5 ? 2 * tw.t * tw.t : 1 - (-2 * tw.t + 2) ** 2 / 2;
      this.world.position.set(tw.fx + (tw.tx - tw.fx) * e, tw.fy + (tw.ty - tw.fy) * e);
      if (tw.t >= 1) this.tween = null;
    }
    if (this.run) {
      const r = this.run;
      r.t = Math.min(1, r.t + dtMs / r.ms);
      const seg = r.t * (r.pts.length - 1), i = Math.min(r.pts.length - 2, Math.floor(seg)), u = seg - i;
      const p0 = r.pts[i]!, p1 = r.pts[i + 1]!;
      r.sprite.position.set(p0.x + (p1.x - p0.x) * u, p0.y + (p1.y - p0.y) * u);
      r.sprite.alpha = 0.6 + 0.4 * Math.sin(r.t * Math.PI);
      if (r.t >= 1) this.stopRun();
    }
    if (this.relayTimers.length && performance.now() - this.lastTimerRefresh > 500) {
      this.lastTimerRefresh = performance.now();
      const now = this.simNow(), scale = Math.max(1, this.view.timeScale);
      for (const rt of this.relayTimers) rt.text.text = countdown((rt.readyAt - now) / scale);
      const k = this.world.scale.x;
      for (const rt of this.relayTimers) rt.text.scale.set(Math.min(1.6, 0.5 / k));
    }
    for (const p of this.pulses) {
      p.t = (p.t + p.speed * dtMs) % 1;
      p.sprite.position.set(p.ax + (p.bx - p.ax) * p.t, p.ay + (p.by - p.ay) * p.t);
      p.sprite.alpha = 0.4 + 0.6 * Math.sin(p.t * Math.PI);
    }
    const now = this.simNow();
    for (const s of this.ships) {
      const span = Math.max(1, s.arriveAt - s.departAt);
      const t = Math.min(1, Math.max(0, (now - s.departAt) / span));
      s.sprite.position.set(s.fromX + (s.toX - s.fromX) * t, s.fromY + (s.toY - s.fromY) * t);
    }
    this.sfx?.tick(dtMs, this.world.scale.x);
    for (const r of this.rings) r.rotation += dtMs * 0.00008;
    for (const g of this.swirls) g.rotation -= dtMs * 0.0012;
    for (const f of this.flashes.slice()) {
      f.t += dtMs / 1600;
      f.g.clear();
      if (f.t >= 1) { f.g.destroy(); this.flashes.splice(this.flashes.indexOf(f), 1); continue; }
      for (let i = 0; i < 3; i++) {
        const u = f.t - i * 0.18;
        if (u <= 0 || u >= 1) continue;
        f.g.circle(f.x, f.y, 14 + u * 90);
        f.g.stroke({ color: 0x7dd3fc, width: 3 * (1 - u) + 0.5, alpha: (1 - u) * 0.9 });
      }
    }
    const pulse = 0.55 + 0.45 * Math.sin(performance.now() / 180);
    for (const eg of this.engagedRings) eg.alpha = pulse;
    if (this.capitalPulse) { const u = (performance.now() % 2600) / 2600; this.capitalPulse.scale.set(1 + u * 0.35); this.capitalPulse.alpha = 0.7 * (1 - u); }
    const sel = this.selected ? this.systemById.get(this.selected) : undefined;
    this.reticle.clear();
    if (sel) {
      const base = 10 + sel.slots * 3 + 28;
      const a0 = (performance.now() / 900) % (Math.PI * 2);
      for (let i = 0; i < 3; i++) { this.reticle.arc(sel.x, sel.y, base, a0 + i * (Math.PI * 2 / 3), a0 + i * (Math.PI * 2 / 3) + 1.1); this.reticle.stroke({ color: 0xffffff, width: 2.5, alpha: 0.9 }); }
    }
    const k = this.world.scale.x;
    if (Math.abs(k - this.lastLabelScale) > 0.01) {
      this.lastLabelScale = k;
      for (const child of this.labelLayer.children) { const lod = (child as Text & { lod: number }).lod ?? 0; child.visible = k >= lod; child.scale.set(Math.min(1.6, 0.5 / k)); }
      this.starfield.alpha = Math.min(1, 0.4 + k);
    }
  }

  // --- camera --------------------------------------------------------------

  private bindCamera(canvas: HTMLCanvasElement): void {
    canvas.style.touchAction = 'none';
    canvas.addEventListener('pointerdown', (e) => { this.touched = true; this.tween = null; this.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY }); this.dragMoved = false; canvas.setPointerCapture(e.pointerId); });
    canvas.addEventListener('pointermove', (e) => {
      const prev = this.pointers.get(e.pointerId);
      if (!prev) return;
      const cur = { x: e.clientX, y: e.clientY };
      if (this.pointers.size === 1) {
        const dx = cur.x - prev.x, dy = cur.y - prev.y;
        if (Math.abs(dx) + Math.abs(dy) > 3) this.dragMoved = true;
        this.world.position.x += dx; this.world.position.y += dy;
      } else if (this.pointers.size === 2) {
        this.pointers.set(e.pointerId, cur);
        const [p1, p2] = [...this.pointers.values()];
        const d = Math.hypot(p1!.x - p2!.x, p1!.y - p2!.y);
        if (this.pinchDist) this.zoomAt((p1!.x + p2!.x) / 2, (p1!.y + p2!.y) / 2, d / this.pinchDist);
        this.pinchDist = d;
        this.dragMoved = true;
        return;
      }
      this.pointers.set(e.pointerId, cur);
    });
    const up = (e: PointerEvent): void => { this.pointers.delete(e.pointerId); if (this.pointers.size < 2) this.pinchDist = 0; };
    canvas.addEventListener('pointerup', up);
    canvas.addEventListener('pointercancel', up);
    canvas.addEventListener('wheel', (e) => { e.preventDefault(); this.touched = true; this.zoomAt(e.clientX, e.clientY, Math.exp(-e.deltaY * 0.0015)); }, { passive: false });
    this.app.stage.eventMode = 'static';
    this.app.stage.hitArea = this.app.screen;
    this.app.stage.on('pointertap', (e) => { if (!this.dragMoved && e.target === this.app.stage) this.cb.onSelect(null); });
  }

  private zoomAt(cx: number, cy: number, factor: number): void {
    const rect = this.app.canvas.getBoundingClientRect();
    const sx = cx - rect.left, sy = cy - rect.top;
    const k0 = this.world.scale.x;
    const k1 = Math.min(1.8, Math.max(0.05, k0 * factor));
    const wx = (sx - this.world.position.x) / k0, wy = (sy - this.world.position.y) / k0;
    this.world.scale.set(k1);
    this.world.position.set(sx - wx * k1, sy - wy * k1);
  }
}

function hexPath(g: Graphics, cx: number, cy: number, size: number): void {
  for (let i = 0; i < 6; i++) {
    const a = (Math.PI / 3) * i;
    const x = cx + size * Math.cos(a), y = cy + size * Math.sin(a);
    if (i === 0) g.moveTo(x, y); else g.lineTo(x, y);
  }
  g.closePath();
}

function dashed(g: Graphics, x1: number, y1: number, x2: number, y2: number, dash: number, gap: number, stroke: { color: number; width: number; alpha: number }): void {
  const len = Math.hypot(x2 - x1, y2 - y1);
  if (len === 0) return;
  const ux = (x2 - x1) / len, uy = (y2 - y1) / len;
  for (let d = 0; d < len; d += dash + gap) {
    const e = Math.min(len, d + dash);
    g.moveTo(x1 + ux * d, y1 + uy * d).lineTo(x1 + ux * e, y1 + uy * e);
  }
  g.stroke(stroke);
}
